import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import AdminLayout from "../../components/admin/AdminLayout";
import { createCampaign, patchCampaign, campaignDetail, previewAudience, sendCampaignNow, sendCampaignTest, scheduleCampaign } from "../../lib/admin-api";
import { Save, Send, Users, ArrowLeft, X, Calendar } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";

const SITIO_TAGS = ["eclipse", "flexshade", "velarias"];
const CIUDADES_OPC = ["Monterrey", "Guadalajara", "CDMX", "Saltillo", "Cancún", "Mérida", "Querétaro", "Chihuahua"];
const FUENTE_OPC = ["cotizacion", "catalogo", "whatsapp", "directo", "newsletter", "manual", "csv_import"];

const DEFAULT_CONTENT = `<h2>Hola {{nombre}},</h2>
<p>Te queremos compartir información de Eclipse Cubiertas sobre nuestras soluciones.</p>
<p><a href="https://eclipsecubiertas.com.mx">Ver más</a></p>`;

export default function AdminCampaignEditor() {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const nav = useNavigate();

    const [form, setForm] = useState({
        name: "",
        subject_a: "",
        subject_b: "",
        ab_split: 0,
        content: DEFAULT_CONTENT,
        segments: { sitio: [], ciudad: [], fuente: [] },
    });
    const [previewVariant, setPreviewVariant] = useState("a");
    const [audience, setAudience] = useState(null);
    const [saving, setSaving] = useState(false);
    const [sendNowOpen, setSendNowOpen] = useState(false);
    const [testOpen, setTestOpen] = useState(false);
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const [status, setStatus] = useState("draft");

    useEffect(() => {
        if (!isEdit) return;
        let alive = true;
        campaignDetail(id).then((d) => {
            if (!alive) return;
            const c = d.campaign;
            setForm({
                name: c.name || "",
                subject_a: c.subject_a || "",
                subject_b: c.subject_b || "",
                ab_split: c.ab_split || 0,
                content: c.content || DEFAULT_CONTENT,
                segments: c.segments || { sitio: [], ciudad: [], fuente: [] },
            });
            setStatus(c.status);
        }).catch(console.error);
        return () => { alive = false; };
    }, [id, isEdit]);

    const toggleSegment = (key, val) => {
        const cur = form.segments[key] || [];
        const next = cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val];
        setForm({ ...form, segments: { ...form.segments, [key]: next } });
    };

    const save = async () => {
        if (!form.name || !form.subject_a) {
            toast.error("Nombre y asunto A son requeridos");
            return;
        }
        setSaving(true);
        try {
            if (isEdit) {
                await patchCampaign(id, form);
                toast.success("Cambios guardados");
            } else {
                const res = await createCampaign(form);
                toast.success("Campaña creada");
                nav(`/admin/campaigns/${res.id}/edit`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error al guardar");
        } finally {
            setSaving(false);
        }
    };

    const calcAudience = async () => {
        if (!isEdit) {
            toast.error("Guarda primero la campaña como borrador");
            return;
        }
        try {
            await patchCampaign(id, { segments: form.segments });
            const res = await previewAudience(id);
            setAudience(res);
        } catch (e) {
            toast.error("Error al calcular audiencia");
        }
    };

    const renderPreview = () => {
        const subj = previewVariant === "a" ? form.subject_a : (form.subject_b || form.subject_a);
        const html = (form.content || "").replace(/\{\{nombre\}\}/g, "[NOMBRE]").replace(/\{\{email\}\}/g, "[EMAIL]");
        return { subj, html };
    };

    const { subj: previewSubject, html: previewHtml } = renderPreview();

    return (
        <AdminLayout
            title={isEdit ? "Editar campaña" : "Nueva campaña"}
            subtitle="Crea contenido, segmenta tu audiencia y programa el envío"
            testid="admin-campaign-editor"
            actions={
                <Link to="/admin/campaigns" className="h-9 px-3 border border-neutral-200 rounded-md text-sm flex items-center gap-2 bg-white hover:bg-neutral-50">
                    <ArrowLeft className="w-4 h-4" /> Volver
                </Link>
            }
        >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Editor */}
                <div className="space-y-4">
                    <div className="bg-white border border-neutral-200 rounded-md p-4 space-y-3">
                        <div><label className="text-xs font-semibold text-neutral-600">Nombre (interno)</label>
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="campaign-form-name" />
                        </div>
                        <div><label className="text-xs font-semibold text-neutral-600">Asunto A *</label>
                            <Input value={form.subject_a} onChange={(e) => setForm({ ...form, subject_a: e.target.value })} data-testid="campaign-form-subject-a" />
                        </div>
                        <div><label className="text-xs font-semibold text-neutral-600">Asunto B (opcional A/B)</label>
                            <Input value={form.subject_b} onChange={(e) => setForm({ ...form, subject_b: e.target.value })} data-testid="campaign-form-subject-b" />
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-neutral-600 flex justify-between"><span>A/B split (% para B)</span><span className="text-eclipse-cta font-mono">{form.ab_split}%</span></label>
                            <input type="range" min={0} max={100} value={form.ab_split} onChange={(e) => setForm({ ...form, ab_split: parseInt(e.target.value) })} className="w-full accent-eclipse-cta" data-testid="campaign-form-absplit" />
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-neutral-600">Contenido HTML</label>
                            <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={12} className="font-mono text-xs" data-testid="campaign-form-content" />
                            <div className="text-[11px] text-neutral-400 mt-1">Variables disponibles: <code>{"{{nombre}}"}</code> <code>{"{{email}}"}</code></div>
                        </div>
                    </div>

                    <div className="bg-white border border-neutral-200 rounded-md p-4 space-y-3">
                        <div className="font-semibold text-sm text-neutral-900">Segmentación</div>
                        <TagToggle label="Sitio" options={SITIO_TAGS} selected={form.segments.sitio || []} onToggle={(v) => toggleSegment("sitio", v)} />
                        <TagToggle label="Ciudad" options={CIUDADES_OPC} selected={form.segments.ciudad || []} onToggle={(v) => toggleSegment("ciudad", v)} />
                        <TagToggle label="Fuente" options={FUENTE_OPC} selected={form.segments.fuente || []} onToggle={(v) => toggleSegment("fuente", v)} />
                        <button onClick={calcAudience} className="h-9 px-4 bg-neutral-900 text-white text-xs font-semibold rounded-md flex items-center gap-2 hover:bg-neutral-800" data-testid="campaign-calc-audience">
                            <Users className="w-4 h-4" /> Calcular audiencia
                        </button>
                        {audience && (
                            <div className="bg-neutral-50 border border-neutral-200 rounded p-3 text-xs">
                                <div>Total: <strong className="text-eclipse-cta text-base">{audience.total}</strong> suscriptores</div>
                                {audience.sample?.length > 0 && (
                                    <div className="mt-2"><div className="text-neutral-500 mb-1">Muestra:</div>
                                        {audience.sample.map((s, i) => <div key={i} className="text-neutral-700">{s.email} {s.nombre && `· ${s.nombre}`}</div>)}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                        <button onClick={save} disabled={saving || status === "sent" || status === "sending"} className="h-10 px-4 bg-eclipse-cta text-white text-sm font-semibold rounded-md flex items-center gap-2 hover:bg-orange-600 disabled:opacity-50" data-testid="campaign-save-btn">
                            <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar borrador"}
                        </button>
                        {isEdit && <button onClick={() => setTestOpen(true)} className="h-10 px-4 border border-neutral-200 rounded-md text-sm flex items-center gap-2 bg-white hover:bg-neutral-50" data-testid="campaign-test-btn">Enviar prueba</button>}
                        {isEdit && status === "draft" && <button onClick={() => setScheduleOpen(true)} className="h-10 px-4 border border-amber-300 bg-amber-50 text-amber-800 rounded-md text-sm flex items-center gap-2"><Calendar className="w-4 h-4" /> Programar</button>}
                        {isEdit && status === "draft" && <button onClick={() => setSendNowOpen(true)} className="h-10 px-4 bg-green-600 text-white text-sm font-semibold rounded-md flex items-center gap-2 hover:bg-green-700" data-testid="campaign-send-now-btn"><Send className="w-4 h-4" /> Enviar ya</button>}
                    </div>
                </div>

                {/* Preview */}
                <div className="space-y-3">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-neutral-600">Vista previa</span>
                        <div className="ml-auto flex gap-1 bg-neutral-100 p-0.5 rounded">
                            <button onClick={() => setPreviewVariant("a")} className={`px-3 py-1 text-xs rounded ${previewVariant === "a" ? "bg-white shadow-sm font-semibold" : ""}`} data-testid="campaign-preview-a">Asunto A</button>
                            {form.subject_b && <button onClick={() => setPreviewVariant("b")} className={`px-3 py-1 text-xs rounded ${previewVariant === "b" ? "bg-white shadow-sm font-semibold" : ""}`}>Asunto B</button>}
                        </div>
                    </div>
                    <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
                        <div className="bg-neutral-50 px-4 py-3 border-b text-xs">
                            <div className="text-neutral-500">Asunto:</div>
                            <div className="font-semibold text-neutral-900 mt-0.5">{previewSubject || "(sin asunto)"}</div>
                        </div>
                        <div className="p-5 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: previewHtml }} />
                    </div>
                </div>
            </div>

            {sendNowOpen && <SendNowModal id={id} onClose={() => setSendNowOpen(false)} onDone={(res) => { setSendNowOpen(false); toast.success(`Enviados: ${res.sent}, fallidos: ${res.failed}, skipped: ${res.skipped}`); setStatus(res.status); }} />}
            {testOpen && <TestModal id={id} onClose={() => setTestOpen(false)} />}
            {scheduleOpen && <ScheduleModal id={id} onClose={() => setScheduleOpen(false)} onDone={() => { setScheduleOpen(false); setStatus("scheduled"); toast.success("Campaña programada"); }} />}
        </AdminLayout>
    );
}

function TagToggle({ label, options, selected, onToggle }) {
    return (
        <div>
            <label className="text-xs text-neutral-500 block mb-1.5">{label}</label>
            <div className="flex flex-wrap gap-2">
                {options.map((o) => {
                    const on = selected.includes(o);
                    return <button key={o} type="button" onClick={() => onToggle(o)} className={`text-xs px-3 py-1.5 rounded-full border transition ${on ? "bg-eclipse-cta text-white border-eclipse-cta" : "bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400"}`} data-testid={`seg-${label.toLowerCase()}-${o}`}>{o}</button>;
                })}
            </div>
        </div>
    );
}

function SendNowModal({ id, onClose, onDone }) {
    const [sending, setSending] = useState(false);
    const exec = async () => {
        setSending(true);
        try {
            const res = await sendCampaignNow(id);
            onDone(res);
        } catch (e) {
            toast.error("Error al enviar");
            setSending(false);
        }
    };
    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white max-w-md w-full rounded-md p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="campaign-sendnow-modal">
                <div className="flex justify-between"><h3 className="text-lg font-bold">¿Enviar campaña ahora?</h3><button onClick={onClose}><X className="w-4 h-4" /></button></div>
                <p className="text-sm text-neutral-600">Esto enviará la campaña a TODOS los suscriptores que coincidan con la segmentación. Esta acción no se puede deshacer.</p>
                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 text-sm border border-neutral-200 rounded-md">Cancelar</button>
                    <button onClick={exec} disabled={sending} className="px-4 py-2 text-sm bg-green-600 text-white rounded-md font-semibold disabled:opacity-50" data-testid="campaign-sendnow-confirm">{sending ? "Enviando…" : "Confirmar envío"}</button>
                </div>
            </div>
        </div>
    );
}

function TestModal({ id, onClose }) {
    const [emails, setEmails] = useState("");
    const [busy, setBusy] = useState(false);
    const exec = async () => {
        const list = emails.split(",").map((e) => e.trim()).filter(Boolean);
        if (list.length === 0) return;
        setBusy(true);
        try {
            const res = await sendCampaignTest(id, list);
            toast.success(`Test enviado a ${res.sent} destinatarios`);
            onClose();
        } catch (e) {
            toast.error("Error en el test");
        } finally {
            setBusy(false);
        }
    };
    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white max-w-md w-full rounded-md p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between"><h3 className="text-lg font-bold">Enviar prueba</h3><button onClick={onClose}><X className="w-4 h-4" /></button></div>
                <p className="text-xs text-neutral-500">Lista de emails separados por coma.</p>
                <Textarea value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="test@example.com, otro@example.com" rows={3} data-testid="campaign-test-emails" />
                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 text-sm border border-neutral-200 rounded-md">Cancelar</button>
                    <button onClick={exec} disabled={busy} className="px-4 py-2 text-sm bg-eclipse-cta text-white rounded-md font-semibold disabled:opacity-50">{busy ? "Enviando…" : "Enviar"}</button>
                </div>
            </div>
        </div>
    );
}

function ScheduleModal({ id, onClose, onDone }) {
    const [when, setWhen] = useState("");
    const exec = async () => {
        if (!when) return;
        try {
            const iso = new Date(when).toISOString();
            await scheduleCampaign(id, iso);
            onDone();
        } catch (e) {
            toast.error("Error al programar");
        }
    };
    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white max-w-md w-full rounded-md p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between"><h3 className="text-lg font-bold">Programar envío</h3><button onClick={onClose}><X className="w-4 h-4" /></button></div>
                <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="w-full h-10 border border-neutral-200 rounded-md px-3 text-sm" data-testid="campaign-schedule-when" />
                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 text-sm border border-neutral-200 rounded-md">Cancelar</button>
                    <button onClick={exec} className="px-4 py-2 text-sm bg-amber-600 text-white rounded-md font-semibold">Programar</button>
                </div>
            </div>
        </div>
    );
}
